class ProductData {
  constructor(loaded, data) {
    this.loaded = loaded
    this.data = data
    return this
  }

  isLoading() {
    return this.loaded === null
  }

  isLoaded() {
    return this.loaded === true
  }

  getCurrencySymbol() {
    const symbol = this.data.recommendedCurrencySymbol
    return symbol || '$'
  }

  getTaxationTextOnTotal() {
    if (!this.data.recommendedCurrency) return ''
    switch (this.data.recommendedCurrency) {
      case 'gbp':
        return '(incl. VAT)'
      default:
        return ''
    }
  }

  getCurrencyCode() {
    const code = this.data.recommendedCurrency
    return code || 'usd'
  }

  getCountryCode() {
    const cc = this.data?.actualCountryCode

    return cc || 'us'
  }

  getProductEnum(quantity) {
    if (quantity && quantity > 1) {
      return 'unlimited_volume'
    } else {
      return 'unlimited'
    }
  }

  getRawUnitPrice(quantity, productEnum) {
    if (!(this.loaded && this.data && quantity)) {
      return 0
    }

    if (!productEnum) {
      productEnum = this.getProductEnum(quantity)
    }

    let rawYearlyPrice = 0
    try {
      const sku =
        this.data.products[productEnum]['year'][this.data.recommendedCurrency]

      if (productEnum === 'unlimited') {
        rawYearlyPrice = sku.rawYearlyPrice
      } else {
        const tiers = sku.tiers
        for (let i = 0; i < tiers.length; i++) {
          const tier = tiers[i]
          if (quantity >= tier.from && (!tier.to || tier.to >= quantity)) {
            rawYearlyPrice = tier.rawYearlyPrice
          }
        }
      }

      return parseInt(rawYearlyPrice) / 100
    } catch (e) {
      console.error("couldn't parse products", e)
      return 0
    }
  }

  prettyNumbers(x) {
    const decimal = x.toFixed(2)
    const rounded = parseInt(x)
    return this.commaDelimitedThousands(rounded === decimal ? rounded : decimal)
  }

  prettyDecimals(x) {
    const decimal = x.toFixed(2)
    return this.commaDelimitedThousands(decimal)
  }

  commaDelimitedThousands(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  getUnitPrice(quantity) {
    const rawUnitPrice = this.getRawUnitPrice(quantity)

    if (!rawUnitPrice) {
      return ''
    } else {
      return this.getCurrencySymbol() + this.prettyNumbers(rawUnitPrice)
    }
  }

  getTotalPrice(quantity) {
    const rawUnitPrice = this.getRawUnitPrice(quantity)
    if (!rawUnitPrice) {
      return ''
    } else {
      return (
        this.getCurrencySymbol() + this.prettyNumbers(rawUnitPrice * quantity)
      )
    }
  }

  isQuantityInRange(quantity) {
    return quantity && quantity >= 1 && !this.isQuantityTooHigh(quantity)
  }

  isQuantityTooHigh(quantity) {
    return quantity > 400
  }

  buildUnlimitedCheckoutPath(quantity) {
    return `/checkout/unlimited?quantity=${quantity}&productId=${this.getProductId(
      quantity
    )}`
  }

  getProductId(quantity = 1) {
    return encodeURIComponent(
      this.data.products[this.getProductEnum(quantity)]['year'][
        this.data.recommendedCurrency
      ]['priceId']
    )
  }

  getRawUnitPriceForPD() {
    return 350
  }

  getPrettyUnitPriceForPD() {
    return (
      this.getCurrencySymbol() + this.prettyNumbers(this.getRawUnitPriceForPD())
    )
  }

  getTermDiscount(years) {
    let intYears = 0

    try {
      intYears = parseInt(years)
    } catch {}

    if (!intYears || intYears > 3) {
      intYears = 1
    }

    if (intYears === 2 || intYears === 3) {
      return 10
    } else {
      return 0
    }
  }

  _getPerUserCost(quantity, productEnum = 'schools') {
    return this.getRawUnitPrice(quantity, productEnum)
  }

  getPerUserCost(quantity, productEnum) {
    return (
      this.getCurrencySymbol() +
      this.prettyNumbers(this._getPerUserCost(quantity, productEnum))
    )
  }

  _getTotalUserCost(quantity, termLength, productEnum) {
    return quantity * termLength * this._getPerUserCost(quantity, productEnum)
  }

  getTotalUserCost(quantity, termLength, productEnum) {
    return (
      this.getCurrencySymbol() +
      this.prettyDecimals(
        this._getTotalUserCost(quantity, termLength, productEnum)
      )
    )
  }

  _getTotalDiscount(quantity, termLength, productEnum) {
    return (
      (this.getTermDiscount(termLength) / 100) *
      this._getTotalUserCost(quantity, termLength, productEnum)
    )
  }

  getTotalDiscount(quantity, termLength, productEnum) {
    return (
      '-' +
      this.getCurrencySymbol() +
      this.prettyDecimals(
        this._getTotalDiscount(quantity, termLength, productEnum)
      )
    )
  }

  _getTotalPDCost(PDHours) {
    return this.getRawUnitPriceForPD() * PDHours
  }

  getTotalPDCost(PDHours) {
    return (
      this.getCurrencySymbol() +
      this.prettyDecimals(this._getTotalPDCost(PDHours))
    )
  }

  getTotalCost(quantity, termLength, PDHours, productEnum) {
    return (
      this.getCurrencySymbol() +
      this.prettyDecimals(
        this._getTotalUserCost(quantity, termLength, productEnum) -
          this._getTotalDiscount(quantity, termLength, productEnum) +
          this._getTotalPDCost(PDHours)
      )
    )
  }
}

export { ProductData }
